import { useTranslation } from "react-i18next";
import { ArrowUpRight, Check, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTAButtons } from "@/components/CTAButtons";
import { IndependenceStrip } from "@/components/IndependenceStrip";
import { CONTACT, waLink, MEDICAL_TOURISM_TREATMENTS } from "@/data/site";

const BASE = `https://${CONTACT.domain}`;

const MedicalTourism = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav.home"), item: `${BASE}/${lang}` },
      { "@type": "ListItem", position: 2, name: t("nav.medicalTourism"), item: `${BASE}/${lang}/medical-tourism` },
    ],
  };

  return (
    <>
      <Seo
        title={t("medicalTourism.seoTitle")}
        description={t("medicalTourism.metaDescription")}
        path="/medical-tourism"
        exact
        jsonLd={[breadcrumbSchema]}
      />
      <PageHero
        eyebrow={t("medicalTourism.eyebrow")}
        title={t("medicalTourism.h1")}
        subtitle={t("medicalTourism.intro")}
        crumbs={[{ label: t("nav.home"), to: "/" }, { label: t("nav.medicalTourism") }]}
      >
        <CTAButtons />
      </PageHero>

      <section className="vc-container py-14 sm:py-16">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              <h2 className="text-2xl font-extrabold text-[hsl(var(--vc-ink))]">{t("medicalTourism.treatmentsTitle")}</h2>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {MEDICAL_TOURISM_TREATMENTS.map((tr) => (
                  <li key={tr} className="flex items-start gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft">
                    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-mint text-brand"><Check className="h-3.5 w-3.5" /></span>
                    <span className="text-sm font-semibold text-[hsl(var(--vc-ink))]">{tr}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal className="mt-10">
              <div className="rounded-2xl border-2 border-brand/25 bg-mint p-6 vc-shadow-soft" data-testid="tourism-vietclinics-cta">
                <h3 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t("medicalTourism.vcTitle")}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{t("medicalTourism.vcText")}</p>
                <Button asChild className="mt-5 bg-brand font-bold text-white hover:opacity-90">
                  <a
                    href="https://www.vietclinics.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tourism-vietclinics-link"
                  >
                    {t("medicalTourism.vcCta")} <ArrowUpRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </Reveal>

            <Reveal className="mt-10"><IndependenceStrip variant="band" /></Reveal>
          </div>

          <aside>
            <div className="lg:sticky lg:top-24 rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-card">
              <h3 className="text-lg font-extrabold text-[hsl(var(--vc-ink))]">{t("cta.getMedical")}</h3>
              <p className="mt-1.5 text-sm text-[hsl(var(--vc-slate))]">Need everyday medical support while in Vietnam? We can help.</p>
              <Button asChild className="mt-4 w-full bg-brand py-5 font-bold text-white hover:opacity-90">
                <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="tourism-whatsapp-btn">
                  <MessageCircle className="mr-2 h-4 w-4" /> {t("cta.whatsappUs")}
                </a>
              </Button>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default MedicalTourism;
